import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import PulseLoader from "react-spinners/PulseLoader"
import EtContentEditorLightWithPlace from "@/utils/EtContentEditorLightWithPlace"
import EtContentEditorLight3 from "@/utils/EtContentEditorLight3"
import { useCreateNewAssessmentMutation } from "./assessmentsApiSlice"
import { useGetSectionsQuery } from "../sections/sectionsApiSlice"

const EtCreateAssessment = ({setActiveAdminView}) => {

  // First Created - 10 Apr 2024
  // Author - Atul Pandey
  // Updated - 19 Aug 2025 - switched from navigate to setActiveAdminView

  // 0 - Set Page Title
  useTitle('Create Assessment Section')
  
  // 1 - set page navigations
  const navigate = useNavigate()

  // 2 - set the mutation to create the new section data
  const[createNewAssessment, {
    isLoading,
    isSuccess,
    isError,
    error,
  }] = useCreateNewAssessmentMutation()


  // 3 - get all sections
  const { sections } = useGetSectionsQuery('sectionList', {
    selectFromResult: ( { data }) => ({
      sections: data?.ids.map(id => data?.entities[id])
    })
  })
  
  console.log('sections:', sections)

  // 4a - New state to hold the currently selected section Id
const [selectedSection, setSelectedSection] = useState("")
const [allSectionsAdded, setAllSectionsAdded] = useState(false)

// 5 - Create options to select a section from list of all sections

const sectionOptions = sections ? [
  <option key="placeholder" value="" disabled selected>
    Select a question set
  </option>,
  ...sections?.map(section => (
    <option key={section.id} value={section.id}>
      {section.sectionName}
    </option>
  ))
] : []

// 6 - create the form data object
const [formData, setFormData] = useState({
  assessmentName: "",
  assessmentIntro: "",
  assessmentOutro: "",
  aboutMe: "",
  pic: "",
  sections: [],
})

// 7 - Reset form upon successful submission
useEffect(() => {
  if(isSuccess) {
    setFormData({
      assessmentName: "",
      assessmentIntro: "",
      assessmentOutro: "",
      aboutMe: "",
      pic: "",
      sections: [],
    })

    // navigate('/assessments')
    setActiveAdminView('assessments'); // 19 Aug 2025
  }

},[isSuccess,navigate])

// 8 - Form field updates / change handling

const handleChange = (e) => {
  const { name, value, files } = e.target
  if (name === "pic") {
    setFormData(prevState => ({ ...prevState, pic: files[0] }))
  } else {
    setFormData(prevState => ({ ...prevState, [name]: value }))
  }
}


// 8a - New function to handle adding selected section
const handleAddSection = () => {
  // Find the full section object based on the selected ID
  const sectionToAdd = sections.find(section => section.id === selectedSection);
  if (sectionToAdd && !formData.sections.some(sec => sec.id === selectedSection)) {
    setFormData(prevState => ({
      ...prevState,
      sections: [...prevState.sections, sectionToAdd]
    }));
    setSelectedSection('');
  }
}

// 8b - New function to handle removing a section
const handleRemoveSection = (sectionId) => {
  setFormData(prevState => ({
    ...prevState,
    sections: prevState.sections.filter(sec => sec.id !== sectionId)
  }))
  
}

// 8c - function to strip html tags from sections
function stripHtml(html) {
  return html.replace(/<\/?[^>]+(>|$)/g, "");
}

// 8d - handle select change for section
const handleSelectChange = (e) => {
  setSelectedSection(e.target.value);
}

useEffect(() => {
  // Check if all questions from the set have been added
  setAllSectionsAdded(sections?.length === formData.sections?.length);
}, [formData.sections, sections?.length]);

// 10 - Form Submit Logic
const handleSubmit = async (e) => {
e.preventDefault()

// Initialize an object to collect the form data
const assessmentData = {
  assessmentName: formData.assessmentName,
  assessmentIntro: formData.assessmentIntro,
  assessmentOutro: formData.assessmentOutro,
  aboutMe: formData.aboutMe,
  sections: formData.sections,
}

// console.log('assessment Name:',assessmentData.assessmentName)

if (formData.pic) {
  // Convert the image to a base64 string
  const reader = new FileReader()
  reader.onloadend = async () => {
    // Once conversion is done, add the base64 string to your data object
    assessmentData.pic = reader.result

    // Now, send the data object to the server
    try {
      console.log("Form submitted.")
      await createNewAssessment(assessmentData).unwrap() // Dispatch the RTK Query mutation
    } catch (error) {
      console.error(error)
    }
  }
  reader.readAsDataURL(formData.pic)
} else {
  // No image, send other data as is
  try {
    console.log("Form submitted.")
    await createNewAssessment(assessmentData).unwrap() // Dispatch the RTK Query mutation
  } catch (error) {
    console.error(error)
  }
}
}

// 11 - Handle Cancel

const handleCancel = () => {
  // navigate("/assessments")
  setActiveAdminView('assessments'); // 19 Aug 2025
}

// 12 - Handle Clear
const handleClear = () => {
  setFormData({
    assessmentName: "",
    assessmentIntro: "",
    assessmentOutro: "",
    aboutMe: "",
    pic: "",
    sections: [],
  })
}
return (
  <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black">
    {isLoading && <PulseLoader color={"#ea580c"} />}
    {isError && (
      <p className="text-orange-700 font-semi-bold py-2">
        {error?.data?.message}
      </p>
    )}
    <div className="bg-white p-1 m-1 rounded">
      <p className="p-1 m-1 font-bold font-poppins text-xl">Add an Assessment</p>

      <div className="bg-gray-200 rounded m-1 p-1">
        <form
          onSubmit={handleSubmit}
          className="m-1 p-1 text-xs font-poppins"
        >
          
          <div id="assessmentName">
            <input
              type="text"
              name="assessmentName"
              placeholder="Enter the assessment name here"
              value={formData.assessmentName}
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
            />
          </div>

          <div className="bg-white w-full rounded p-2 m-1">
            <EtContentEditorLightWithPlace
              value={formData.assessmentIntro}
              handleChange={(content) =>
                setFormData({ ...formData, assessmentIntro: content })
              }
              placeholderText={
                "Write what people would see when this assessment starts!"
              }
              // toolbarId={'sectionIntro'}
            />
          </div>

          <div className="bg-white w-full rounded p-2 m-1">
            <EtContentEditorLight3
              value={formData.assessmentOutro}
              handleChange={(content) =>
                setFormData({ ...formData, assessmentOutro: content })
              }
              placeholderText={
                "Write what people would see when this assessment ends!"
              }
              toolbarId={"assessmentOurtro"}
            />
          </div>

          <div id="aboutMe">
            <input
              type="text"
              name="aboutMe"
              placeholder="Add something about the personality about this assessment"
              value={formData.aboutMe}
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
            />
          </div>

          <div id="pic">
            <input
              type="file"
              name="pic"
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
            />
          </div>

          {/* Section Selector and Add Button */}
          <div>
            <div className="flex justify-between items-center my-2">
              <select
                onChange={handleSelectChange}
                value={selectedSection}
                className="w-full rounded p-2 mr-2"
              >
                <option value="" disabled>
                  Select a section
                </option>
                {/* {sections?.map((section) => (
                  <option key={section.id} value={section.id}>
                    {stripHtml(section.sectionName)}
                  </option>
                ))} */}
                {sectionOptions}
              </select>
              {!allSectionsAdded && (
              <button
                type="button"
                onClick={handleAddSection}
                className="py-2 px-4 rounded bg-orange-600 text-white"
              >
                +
              </button>)}
            </div>

            {/* Display Selected Sections with Remove Option */}
            {formData.sections?.map((sectionId, index) => (
              <div
                key={index}
                className="flex justify-between items-center bg-gray-100 p-2 rounded my-1"
              >
                <span className="text-black">
                  {stripHtml(sectionId.sectionName)}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveSection(sectionId.id)}
                  className="py-1 px-3 rounded bg-red-500 text-white"
                >
                  -
                </button>
              </div>
            ))}
          </div>

          {/* 10 Apr 2024 - Question Options */}

          <div id="actions" className="bg-white m-1 px-1 py-3 rounded">
            <EtBtnFormBasic
              name={"Create"}
              type={"create"}
              handleClick={handleSubmit}
            />
            <EtBtnFormBasic
              name={"Clear"}
              type={"clear"}
              handleClick={handleClear}
            />
            <EtBtnFormBasic
              name={"Cancel"}
              type={"cancel"}
              handleClick={handleCancel}
            />
          </div>
        </form>
      </div>
    </div>
  </div>
)
}

export default EtCreateAssessment